import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';

import { normalizePhone } from './normalizePhone';

/**
 * Wire-format contract with the 1C backend: phone numbers travel as bare
 * digits with country code and no `+` — e.g. `380673326652`, `48601234567`.
 *
 * This module is the RN-portable validation/formatting layer — keep it free
 * of widget- and DOM-specific imports.
 */

/**
 * The parts an international phone widget splits a number into. Declared
 * structurally rather than imported from `antd-phone-input` so this module
 * stays widget-free and portable to RN.
 */
export interface IPhoneNumberParts {
	countryCode?: number | null;
	areaCode?: string | null;
	phoneNumber?: string | null;
}

/**
 * Composes widget parts into the backend wire format — digits with country
 * code and no `+`. Returns `null` when no subscriber number has been entered.
 *
 * A plain string is accepted too, so a value loaded from the backend can be
 * passed through before the widget has replaced it with its own parts.
 */
export const toWireDigits = (value?: IPhoneNumberParts | string | null): string | null => {
	if (!value) return null;

	if (typeof value === 'string') return value.replace(/\D/g, '') || null;

	const subscriberDigits = `${value.areaCode ?? ''}${value.phoneNumber ?? ''}`.replace(/\D/g, '');
	if (!subscriberDigits) return null;

	return `${value.countryCode ?? ''}${subscriberDigits}`;
};

/**
 * Validates wire-format digits (`48601234567`) against libphonenumber's
 * per-country rules.
 */
export const isValidInternationalPhone = (digits: string): boolean =>
	/^\d+$/.test(digits) && isValidPhoneNumber(`+${digits}`);

/**
 * Formats wire-format digits for display: `380673326652` → `+380 67 332 6652`.
 * Legacy Ukrainian national values (`0XXXXXXXXX`, `80XXXXXXXXX`) are upgraded
 * via `normalizePhone` before formatting; anything unparseable is returned
 * unchanged.
 */
export const formatInternationalPhone = (raw: string): string => {
	const digits = (raw ?? '').replace(/\D/g, '');
	if (!digits) return raw ?? '';

	const parsed = parsePhoneNumberFromString(`+${digits}`);
	if (parsed?.isValid()) return parsed.formatInternational();

	const upgraded = normalizePhone(digits);
	if (upgraded) {
		const reparsed = parsePhoneNumberFromString(`+${upgraded}`);
		if (reparsed?.isValid()) return reparsed.formatInternational();
	}

	return raw;
};
